import React, { Component, Fragment } from 'react';
import { compose, Query } from '../../reactApolloCompat';
import gql from 'graphql-tag';
import cx from 'classnames';
import moment from 'moment';
import cloneDeep from 'lodash.clonedeep';
import withStyles from '@mui/styles/withStyles';
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress } from '@mui/material';
import Hidden from '../../components/Hidden';
import Grid from '../../components/layout/Grid';
import FlexGrid from '../../components/layout/FlexGrid';
import TextField from '../../components/form/TextField';
import Inline, { inlineAlignment } from '../../components/layout/Inline';
import Checkbox from '../../components/form/Checkbox';
import Button from '../../components/form/Button';
import Select from '../../components/form/Select';
import Spinner from '../../components/loading/Spinner';
import ThumbnailCard from '../../components/cards/ThumbnailCard';
import UploadField, { getFileName } from '../../components/form/UploadField';
import createForm from '../../utils/createForm';
import { getClient } from '../../utils/apollo';
import HtmlEditor, { applyShortcodes } from '../../components/form/HtmlEditor';
import CommunicationNoteTemplate from '../../fragments/CommunicationNoteTemplate';
import CommunicationNote from '../../fragments/CommunicationNote';
import { withSnackbarMessage } from '../../context/SnackbarMessage';
import { niceDateTimeFromString, shortDateTimeFromString } from '../../utils/date';
import { joinDefined } from '../../utils/strings';
import Table, { Cell, HeaderRow, Row } from '../../components/table/Table';
import { CloseIcon, EditIcon, EnvelopeIcon, PayableIcon, SaveIcon, TickIcon } from '../../components/IconIndex';
import AuthorisedContact from '../../fragments/AuthorisedContact';
import { getProperty } from '../../utils/objects';

class CommunicationNotes extends Component {
    state = {
        communicationNoteTemplates: [],
        savingCommunicationNote: false,
        sendingCommunicationNote: false,
        open: false
    };
    form = createForm(this, {});

    static getDerivedStateFromProps(newProps, oldState) {
        if (newProps.noteID !== oldState.noteID) {
            return { open: newProps.noteID };
        }
        return null;
    }

    render() {
        const { recordID, recordType, modalOnly } = this.props;
        const client = getClient();
        const variables = 'CremationOrder' === recordType
            ? { creID: recordID, plqID: -1 }
            : { creID: -1, plqID: recordID }
        ;
        const renderResult = ({ data, loading, error, refetch, networkStatus }) => {
            if (error) return 'error';
            this.refetchList = refetch;
            const result =
                !!data && !!data.readCommunicationNotes && data.readCommunicationNotes.edges.map(e => e.node);
            return this.renderNotesTable({ loading, networkStatus }, result);
        };
        const renderLookup = (showList = true) => {
            return !!recordID && (
                <Query query={queryMyBothNotes} variables={variables} client={client}>
                    {result => {
                        if (!(result && result.data)) return null;
                        const list = {};
                        if (Object.keys(result.data).length > 0) {
                            const edges = [];
                            const allEdges = [].concat(
                                result.data.creNotes.edges,
                                result.data.plqNotes.edges
                            );
                            allEdges.forEach(
                                edge =>
                                    edges.findIndex(({ node }) => node.ID === edge.node.ID) < 0 &&
                                    edges.push(edge)
                            );
                            list.readCommunicationNotes = { edges };
                        }
                        return (
                            <>
                                {this.renderNoteModal({
                                    CremationOrder: result.data.creRecord,
                                    PlaqueOrder: result.data.plqRecord
                                })}
                                {!!showList && renderResult({
                                    ...result,
                                    data: list
                                })}
                            </>
                        );
                    }}
                </Query>
            );
        };

        return (!!modalOnly ? renderLookup(false) : (
            <Grid container bucket>
                <Grid item>
                    <h4>Communication Notes</h4>
                </Grid>
                <Grid item>
                    {!recordID && (
                        <p>
                            Communication notes can be added after the{' '}
                            {recordType ? recordType.toLowerCase() : 'record'} is saved.
                        </p>
                    )}
                    {renderLookup(true)}
                </Grid>
                {!!recordID && (
                    <Grid item>
                        <Button variant="primary" onClick={() => this.onOpen(0)}>
                            <PayableIcon />
                            Add new note...
                        </Button>
                    </Grid>
                )}
            </Grid>
        ));
    }

    renderNotesTable = ({ loading, networkStatus }, data) => {
        const { classes } = this.props;
        if (networkStatus < 2) {
            if (loading) return <LinearProgress />;
        }
        if (!data || !data.length) {
            return <p>There are currently no communication notes.</p>;
        }
        return (
            <>
                <Table>
                    <HeaderRow pad>
                        <Cell>Sent</Cell>
                        <Cell>Subject</Cell>
                        <Cell></Cell>
                    </HeaderRow>
                    {data.map((row, index) => {
                            const isSent = !!this.getIsSent(row);
                            const isSentEmail = !!row.EmailSent;
                            const toWho = row.SentEmailTo;
                            return (
                                <Row pad key={index}>
                                    <Cell dataLabel="Sent" className="icon">
                                        {isSent ? <TickIcon className={classes.tickIcon} /> : ''}
                                    </Cell>
                                    <Cell dataLabel="Subject"><strong>{row.Title}</strong><br />
                                        <small>
                                            {!isSent
                                                ? 'Draft only'
                                                : (isSentEmail ? 'Emailed ' : 'Posted ') +
                                                shortDateTimeFromString(
                                                    row.EmailSentAt ||
                                                    joinDefined([row.MailDate, row.MailTime], ' ')
                                                ) +
                                                (!!toWho ? ` to ${toWho} ` : '')}
                                        </small>
                                    </Cell>
                                    <Cell className="icon">
                                        <IconButton className="icon" onClick={() => this.onOpen(row.ID)} size="large">
                                            <EditIcon />
                                        </IconButton>
                                    </Cell>
                                </Row>
                            );
                        }
                    )}
                </Table>
            </>
        );
    };

    renderNoteModal(records) {
        const { open } = this.state;
        if (!open) {
            return this.renderModal(records);
        }
        const variables = { noteId: open };
        return (
            <Query query={queryCommunicationNote} variables={variables} client={getClient()}>
                {({ data, error, loading, refetch }) => {
                    const oldID = this.form.getField('ID');
                    const newID = !!data && !!data.readOneCommunicationNote && data.readOneCommunicationNote.ID;
                    if (newID) {
                        if (newID !== oldID) {
                            this.form.original = cloneDeep(data.readOneCommunicationNote);
                            this.form.state = cloneDeep(data.readOneCommunicationNote);
                        } else {
                            const oldStamp = this.form.getField('LastEdited');
                            const newStamp = data.readOneCommunicationNote.LastEdited;
                            if (newStamp !== oldStamp) {
                                this.form.original = cloneDeep(data.readOneCommunicationNote);
                                this.form.state = cloneDeep(data.readOneCommunicationNote);
                            }
                        }
                    }
                    this.refetchModal = refetch;
                    return this.renderModal(records, loading);
                }}
            </Query>
        );
    }

    renderModal(records, loading) {
        const { classes, closeModalFunc } = this.props;
        const { open } = this.state;
        if (!closeModalFunc && open === false) return null;
        const handleClose = (event, reason) => {
            if (reason && reason === 'backdropClick') {
                return; // Do nothing if the backdrop is clicked
            }
            this.onClose(event);
        };
        return (
            <Dialog
                classes={{ paper: cx(classes.root, 'dialog-modal modal') }}
                open={!!closeModalFunc || open !== false}
                onClose={handleClose}
                disableEnforceFocus
                disableEscapeKeyDown
            >
                {!!loading && <LinearProgress />}
                {this.renderNoteDetails(records)}
            </Dialog>
        );
    }

    renderFooter = () => {
        const { savingCommunicationNote, sendingCommunicationNote } = this.state;
        const { state: noteObj, original } = this.form;
        const readonlyCommunicationNote = this.getIsSent(original);

        const disabledSendMailButton =
            !!savingCommunicationNote ||
            !!sendingCommunicationNote ||
            !!noteObj.EmailSent ||
            !!this.form.getDirty().length ||
            !noteObj.ID ||
            !noteObj.Title ||
            !noteObj.Description ||
            !noteObj.SendEmail ||
            !(noteObj.EmailApplicant || noteObj.EmailBusiness || noteObj.EmailAuthorised || (noteObj.EmailOther && noteObj.OtherEmailAddress));

        const disabledSaveButton =
            !!savingCommunicationNote || !!sendingCommunicationNote || !this.form.getDirty().length;

        let saveButtonIcon = <SaveIcon />;
        let saveButtonText = !this.form.getDirty().length && this.form.getField('ID') ? 'Saved' : 'Save Changes';
        let sendButtonIcon = <EnvelopeIcon />;
        let sendButtonText = 'Send Email';

        if (!!sendingCommunicationNote) {
            sendButtonIcon = <Spinner size="xs" />;
            sendButtonText = 'Sending';
        } else if (!!savingCommunicationNote) {
            saveButtonIcon = <Spinner size="xs" />;
            saveButtonText = 'Saving';
        }

        return (
            <Inline alignment={inlineAlignment.rightAlignSiblings} style={{ width: '100%' }}>
                <div>
                    <Button variant="secondary" onClick={this.onClose} startIcon={<CloseIcon />}>
                        Close
                    </Button>
                </div>

                {!readonlyCommunicationNote && (
                    <Inline alignment={inlineAlignment.rightAlignSiblings}>
                        <div />
                        {!!noteObj.SendEmail && (
                            <Button
                                variant="urgent"
                                disabled={disabledSendMailButton}
                                onClick={this.onSendEmail}
                                startIcon={sendButtonIcon}
                            >
                                {sendButtonText}
                            </Button>
                        )}
                        <Button
                            variant="confirmation"
                            disabled={disabledSaveButton}
                            onClick={this.onSave}
                            startIcon={saveButtonIcon}
                        >
                            {saveButtonText}
                        </Button>
                    </Inline>
                )}
            </Inline>
        );
    };

    renderNoteDetails(records) {
        const { classes, recordType, recordKey, recordID, substitutionFields } = this.props;

        const form = this.form;
        const { original } = form;
        if (!original) return null;
        const attachments = form.getField('Attachments') || [];

        const readonlyCommunicationNote = this.getIsSent(original);
        const sentPostal = this.getIsMailed(original);

        const displayAttachmentsHeading = !readonlyCommunicationNote || (!!attachments && !!attachments.length);

        const applicantEmail =
            getProperty(records, 'CremationOrder.Client.Email') || getProperty(records, 'PlaqueOrder.Client.Email');
        const noClientEmail = !applicantEmail;

        const businessEmail =
            getProperty(records, 'CremationOrder.Business.Email') || getProperty(records, 'PlaqueOrder.Business.Email');
        const noBusinessEmail = !businessEmail;

        const authorisedEmails = (getProperty(records, 'CremationOrder.AuthorisedContacts') || getProperty(records, 'PlaqueOrder.AuthorisedContacts') || [])
            .reduce((list, item) => (item && item.Email ? list + item.Email + ', ' : ''), '')
            .replace(/,\s$/, '');
        const noAuthorisedContactEmail = !authorisedEmails;

        const applicantPostal =
            getProperty(records, 'CremationOrder.Client.MailingAddress') || getProperty(records, 'PlaqueOrder.Client.MailingAddress');
        const noClientPostal = !applicantPostal;

        const authorisedPostals = (getProperty(records, 'CremationOrder.AuthorisedContacts') || getProperty(records, 'PlaqueOrder.AuthorisedContacts') || [])
            .reduce((list, item) => (item && item.MailingAddress ? list + item.MailingAddress + ', ' : ''), '')
            .replace(/,\s$/, '');
        const noAuthorisedContactPostals = !authorisedPostals;

        const applicantLabel = 'Enquiry' !== recordType ? 'Applicant' : 'Enquirer';
        const businessLabel = 'CremationOrder' !== recordType ? 'Business' : 'Funeral Directors';
        const recordTypeTitle = recordType.replace(/([a-z]+)([A-Z])/, '$1 $2');
        return (
            <>
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0,
                        overflow: 'hidden',
                        pointerEvents: 'none'
                    }}
                >
                    <PayableIcon
                        style={{
                            opacity: 0.025,
                            width: 300,
                            height: 300,
                            position: 'absolute',
                            right: -24,
                            top: 124,
                            transform: 'rotate(20deg)',
                            zIndex: 0
                        }}
                    />
                </div>
                <DialogTitle className={classes.diagHeader}>
                    <Hidden smDown>
                        <PayableIcon className={classes.diagHeaderIcon} />
                        <div className={classes.diagHeaderTitle}>
                            {recordTypeTitle} {recordKey || <> #{recordID}</>}
                        </div>
                        <div className={classes.diagHeaderSubtitle}>Communication Note</div>
                    </Hidden>
                    <Hidden smUp>
                        <div className={classes.diagHeaderTitle}>Communication Note</div>
                    </Hidden>
                </DialogTitle>

                <DialogContent className={classes.content}>
                    <Grid container>
                        {!readonlyCommunicationNote && (
                            <Grid bucket>
                                <Grid item xs={12}>
                                    <h4>Prefill from template</h4>
                                </Grid>
                                <Grid item xs={12}>
                                    <Query query={queryCommunicationNoteTemplates} client={getClient()}>
                                        {results => this.renderCommunicationNoteTemplateList(results)}
                                    </Query>
                                </Grid>
                            </Grid>
                        )}
                        <Grid bucket>
                            {!!readonlyCommunicationNote && (
                                <>
                                    {!!form.getField('EmailSent') && (
                                        <Fragment>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    label="Email To"
                                                    value={
                                                        form.getField('SentEmailTo') ||
                                                        (!!form.getField('EmailApplicant') && applicantEmail) ||
                                                        (!!form.getField('EmailAuthorised') && authorisedEmails)
                                                    }
                                                    readOnly
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    label="Email Sent"
                                                    value={niceDateTimeFromString(form.getField('EmailSentAt'))}
                                                    readOnly
                                                />
                                            </Grid>
                                        </Fragment>
                                    )}

                                    {sentPostal && (
                                        <Fragment>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    label="Mail To"
                                                    value={
                                                        (!!form.getField('MailApplicant') && applicantPostal) ||
                                                        (!!form.getField('MailAuthorised') && authorisedPostals) ||
                                                        (!!form.getField('MailOther') &&
                                                            form.getField('OtherMailAddress'))
                                                    }
                                                    readOnly
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    label="Posted"
                                                    value={niceDateTimeFromString(
                                                        joinDefined(
                                                            [form.getField('MailDate'), form.getField('MailTime')],
                                                            'T'
                                                        )
                                                    )}
                                                    readOnly
                                                />
                                            </Grid>
                                        </Fragment>
                                    )}
                                </>
                            )}

                            {!readonlyCommunicationNote && (
                                <Grid item><h4>Compose the note</h4></Grid>
                            )}
                            {!sentPostal && (
                                <Grid item><TextField
                                    required
                                    label="Subject"
                                    placeholder="Enter subject of note..."
                                    form={form}
                                    name="Title"
                                    readOnly={readonlyCommunicationNote}
                                    InputLabelProps={{ shrink: true }}
                                /></Grid>
                            )}
                            <Grid item>
                                <HtmlEditor
                                    label="Body"
                                    // placeholder="Enter body of note, or choose a new template..."
                                    shortcodes={substitutionFields}
                                    required
                                    form={form}
                                    name="Description"
                                    readOnly={readonlyCommunicationNote}
                                />
                            </Grid>

                            {!readonlyCommunicationNote && (
                                <>
                                    {!!displayAttachmentsHeading && (
                                        <Grid item>
                                            <h4>Attach a File</h4>
                                        </Grid>
                                    )}
                                    <Grid item>
                                        <UploadField
                                            folderPath={this.getDocumentPath()}
                                            onComplete={({ uploadFile }) => this.handleUploadAttachment(uploadFile, attachments)}
                                        />
                                    </Grid>
                                </>
                            )}

                            {!!attachments &&
                                !!attachments.length &&
                                this.renderAttachmentLinks(attachments, readonlyCommunicationNote)}
                        </Grid>

                        {!readonlyCommunicationNote && (
                            <Grid bucket>
                                <Grid item>
                                    <Inline center>
                                        <h4>Send note as email</h4>
                                        <Checkbox
                                            label="Yes, email this note"
                                            form={form}
                                            name="SendEmail"
                                            disabled={readonlyCommunicationNote}
                                            onChange={(evt, val) => !!val && form.setField({ SendMail: false })}
                                        />
                                    </Inline>
                                    {!!form.getField('SendEmail') && (
                                        <Fragment>
                                            <div>
                                                <Checkbox
                                                    label={
                                                        <>
                                                            Send to the {applicantLabel}
                                                            {applicantEmail ? (
                                                                <>
                                                                    : <small>{applicantEmail}</small>
                                                                </>
                                                            ) : (
                                                                <>'s email address</>
                                                            )}
                                                        </>
                                                    }
                                                    form={form}
                                                    name="EmailApplicant"
                                                    disabled={readonlyCommunicationNote || noClientEmail}
                                                />
                                            </div>

                                            <div>
                                                <Checkbox
                                                    label={
                                                        <>
                                                            Send to the {businessLabel}
                                                            {businessEmail ? (
                                                                <>
                                                                    : <small>{businessEmail}</small>
                                                                </>
                                                            ) : (
                                                                <>'s email address</>
                                                            )}
                                                        </>
                                                    }
                                                    form={form}
                                                    name="EmailBusiness"
                                                    disabled={readonlyCommunicationNote || noBusinessEmail}
                                                />
                                            </div>

                                            <div>
                                                <Checkbox
                                                    label={
                                                        <>
                                                            Send to the Authorised Contact
                                                            {authorisedEmails ? (
                                                                <>
                                                                    (s): <small>{authorisedEmails}</small>
                                                                </>
                                                            ) : (
                                                                ' email address'
                                                            )}
                                                        </>
                                                    }
                                                    form={form}
                                                    name="EmailAuthorised"
                                                    disabled={readonlyCommunicationNote || noAuthorisedContactEmail}
                                                />
                                            </div>

                                            <div>
                                                <Checkbox
                                                    label="Send to other email addresses"
                                                    form={form}
                                                    name="EmailOther"
                                                    disabled={readonlyCommunicationNote}
                                                />
                                            </div>

                                            {!!form.getField('EmailOther') &&
                                                (!readonlyCommunicationNote ||
                                                    !!form.getField('OtherEmailAddress')) && (
                                                    <TextField
                                                        label="Other email addresses"
                                                        form={form}
                                                        name="OtherEmailAddress"
                                                        readOnly={readonlyCommunicationNote}
                                                    />
                                                )}
                                        </Fragment>
                                    )}
                                </Grid>
                            </Grid>
                        )}

                        {!readonlyCommunicationNote && (
                            <Grid bucket>
                                <Grid item>
                                    <Inline center>
                                        <h4>Send note as letter</h4>
                                        <Checkbox
                                            label="Yes, mail to a postal address"
                                            form={form}
                                            name="SendMail"
                                            disabled={readonlyCommunicationNote}
                                            onChange={(evt, val) => !!val && form.setField({ SendEmail: false })}
                                        />
                                    </Inline>
                                    {!!form.getField('SendMail') && (
                                        <Fragment>
                                            {(!readonlyCommunicationNote || !!form.getField('MailApplicant')) && (
                                                <div>
                                                    <Checkbox
                                                        label={
                                                            <>
                                                                Send to the {applicantLabel}
                                                                {applicantPostal ? (
                                                                    <>
                                                                        : <small>{applicantPostal}</small>
                                                                    </>
                                                                ) : (
                                                                    <>'s postal address</>
                                                                )}
                                                            </>
                                                        }
                                                        form={form}
                                                        name="MailApplicant"
                                                        disabled={readonlyCommunicationNote || noClientPostal}
                                                    />
                                                </div>
                                            )}

                                            {(!readonlyCommunicationNote || !!form.getField('MailAuthorised')) && (
                                                <div>
                                                    <Checkbox
                                                        label={
                                                            <>
                                                                Send to the Authorised Contact
                                                                {authorisedPostals ? (
                                                                    <>
                                                                        (s): <small>{authorisedPostals}</small>
                                                                    </>
                                                                ) : (
                                                                    ' postal address'
                                                                )}
                                                            </>
                                                        }
                                                        form={form}
                                                        name="MailAuthorised"
                                                        disabled={
                                                            readonlyCommunicationNote || noAuthorisedContactPostals
                                                        }
                                                    />
                                                </div>
                                            )}

                                            {(!readonlyCommunicationNote || !!form.getField('MailOther')) && (
                                                <div>
                                                    <Checkbox
                                                        label="Send to other postal address"
                                                        form={form}
                                                        name="MailOther"
                                                        readOnly={readonlyCommunicationNote}
                                                    />
                                                </div>
                                            )}

                                            {!!form.getField('MailOther') &&
                                                (!readonlyCommunicationNote || !!form.getField('OtherMailAddress')) && (
                                                    <TextField
                                                        label="Other postal address"
                                                        form={form}
                                                        name="OtherMailAddress"
                                                        multiline
                                                        readOnly={readonlyCommunicationNote}
                                                    />
                                                )}
                                        </Fragment>
                                    )}
                                </Grid>

                                {!!form.getField('SendMail') && (
                                    <Fragment>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Posted Date"
                                                type="date"
                                                form={form}
                                                name="MailDate"
                                                disabled={
                                                    !(
                                                        !!form.getField('MailApplicant') ||
                                                        !!form.getField('MailAuthorised') ||
                                                        (!!form.getField('MailOther') &&
                                                            !!form.getField('OtherMailAddress'))
                                                    )
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Posted Time"
                                                type="time"
                                                form={form}
                                                name="MailTime"
                                                disabled={
                                                    !(
                                                        !!form.getField('MailApplicant') ||
                                                        !!form.getField('MailAuthorised') ||
                                                        (!!form.getField('MailOther') &&
                                                            !!form.getField('OtherMailAddress'))
                                                    )
                                                }
                                            />
                                        </Grid>
                                    </Fragment>
                                )}
                            </Grid>
                        )}
                        {form.getField('ID') > 0 && (
                            <Grid item xs={12} style={{ margin: '10px 10px 0 8px' }}>
                                {form.getField('CreatedBy.ID') > 0 && (
                                    <Grid item>
                                        <TextField
                                            readOnly
                                            label={'Created'}
                                            value={this.showAuthor(
                                                form.getField('CreatedBy'),
                                                form.getField('Created')
                                            )}
                                        />
                                    </Grid>
                                )}
                                {!!original.Template && !!original.Template.ID > 0 && (
                                    <Grid item>
                                        <TextField
                                            label="Communication Template"
                                            readOnly
                                            value={`#${original.Template.ID}: ${original.Template.Title}`}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>

                <DialogActions className={cx(classes.diagActions, 'modal-footer', 'paper')}>
                    {this.renderFooter()}
                </DialogActions>
            </>
        );
    }

    renderCommunicationNoteTemplateList = ({ error, loading, data }) => {
        const { communicationNoteTemplates } = this.state;
        const form = this.form;
        const readonlyCommunicationNote = this.getIsSent(form.original);
        const { recordType, templateID } = this.props;
        const hasTemplateID = form.getField('Template.ID') > 0;

        if (error) {
            return 'Error!';
        }

        if (loading) {
            return <span>Loading...</span>;
        }

        if (!data) {
            return '';
        }

        const templates = data.readCommunicationNoteTemplates.edges;

        let selectTemplateOptions = [];

        for (let x = 0; x < templates.length; x++) {
            if (templates[x].node.AddLinkToFuneralArranger && recordType !== 'Funeral') {
                continue;
            }
            selectTemplateOptions.push({
                label: '#' + templates[x].node.ID + ': ' + templates[x].node.Title,
                value: templates[x].node.ID
            });
            communicationNoteTemplates[templates[x].node.ID] = templates[x].node;
        }

        if (templateID && !hasTemplateID) {
            setTimeout(() => {
                form.setField({ 'Template.ID': templateID });
                this.handleCommunicationNoteTemplateChange({ target: { value: templateID } });
            }, 100);
        }

        return (
            <Select
                label="Communication Template"
                placeholder="Choose a template..."
                name="Template.ID"
                options={selectTemplateOptions}
                onChange={this.handleCommunicationNoteTemplateChange}
                form={form}
                readOnly={readonlyCommunicationNote}
            />
        );
    };

    getDocumentPath() {
        return `Uploads/CommunicationNotes/CM/${this.form.getField('ID')}`;
    }

    clearState() {
        this.setState({
            communicationNoteTemplates: [],
            savingCommunicationNote: false,
            sendingCommunicationNote: false
        });
    }

    getIsSent(row) {
        return !!(row.EmailSent || this.getIsMailed(row));
    }

    getIsMailed(row) {
        const mailStamp = moment(row.MailDate + ' ' + row.MailTime);
        return mailStamp && mailStamp.isValid() && mailStamp.isBefore();
    }

    showAuthor(author, date) {
        const { FirstName, Surname, Email } = author || {};
        const Name = joinDefined([FirstName, Surname], ' ');
        return joinDefined(['By', Name || Email || 'Unknown', 'on', niceDateTimeFromString(date)], ' ');
    }

    onClose = () => {
        const { closeModalFunc } = this.props;
        this.form = createForm(this, {});
        this.refetchModal = () => null;
        this.setState({ open: false });
        !!closeModalFunc && closeModalFunc();
    };

    onOpen = noteID => {
        this.setState({ open: noteID || 0, LockoutWarning: false });
    };

    onSave = () => {
        const { onSave, setSnackbarMessage } = this.props;
        const { sendingCommunicationNote } = this.state;

        if (onSave) {
            onSave(this.form.state);
        }

        this.setState({
            savingCommunicationNote: true
        });

        this.saveCommunicationNoteFunction().then(
            ({ data }) => {
                this.setState({
                    savingCommunicationNote: false
                });

                if (!!data) {
                    if ((!!this.form.state.ID && !!data.updateCommunicationNote) || !!data.createCommunicationNote) {
                        const result = data.createCommunicationNote || data.updateCommunicationNote;
                        setSnackbarMessage('Success, your changes have been saved.', true);
                        if (!!result) this.setState({ open: result.ID });
                        else this.refetchModal();

                        if (sendingCommunicationNote) {
                            this.onSendEmail();
                        }
                    } else {
                        console.error('unexpected result: ', data);
                    }
                } else {
                    this.onGqlError('Failed to save communication note');
                }
                this.refetchList();
                this.clearState();
            },
            error => {
                this.clearState();
                this.onGqlError('Failed to save communication note', error);
            }
        );
    };

    onSendEmail = () => {
        const { onSendEmail, setSnackbarMessage } = this.props;

        if (onSendEmail) {
            onSendEmail(this.form.state);
        }

        this.setState({
            sendingCommunicationNote: true
        });

        this.sendCommunicationNoteFunction().then(
            ({ data }) => {
                this.setState({
                    sendingCommunicationNote: false
                });

                if (!!data) {
                    if (
                        !!this.form.state.ID &&
                        !!data.updateCommunicationNote &&
                        data.updateCommunicationNote.EmailSent
                    ) {
                        setSnackbarMessage('Success, your email has been sent.', true);
                        this.onClose();
                    } else {
                        setSnackbarMessage('Oops, your email was not sent. Do you have a recipient email address?');
                    }
                    this.refetchModal();
                }
                this.refetchList();
                this.clearState();
            },
            error => {
                this.clearState();
                this.onGqlError('Failed to send communication note!', error);
            }
        );
    };

    onGqlError = (action, error) => {
        const { message } = error;
        const { setSnackbarMessage } = this.props;
        setSnackbarMessage(joinDefined([action, message], '\n'));
    };

    saveCommunicationNoteFunction = async () => {
        const { recordID, recordType } = this.props;
        const form = this.form;
        const data = {
            [recordType + 'ID']: recordID,
            Title: form.state.Title,
            Description: form.state.Description,
            SendEmail: form.state.SendEmail,
            EmailApplicant: form.state.EmailApplicant,
            EmailBusiness: form.state.EmailBusiness,
            EmailAuthorised: form.state.EmailAuthorised,
            EmailOther: form.state.EmailOther,
            OtherEmailAddress: form.state.OtherEmailAddress,
            SendMail: form.state.SendMail,
            MailApplicant: form.state.MailApplicant,
            MailAuthorised: form.state.MailAuthorised,
            MailOther: form.state.MailOther,
            OtherMailAddress: form.state.OtherMailAddress,
            MailDate: form.state.MailDate,
            MailTime: form.state.MailTime,
            Attachments: [],
            ScheduledToSendEmail: false, // always false, need to save before we can send
            TemplateID: null
        };

        if (!!form.state.Template && !!form.state.Template.ID) {
            data.TemplateID = form.state.Template.ID;
        }

        let mutation = createCommunicationNote;
        if (!!form.state.ID) {
            data.ID = form.state.ID;
            mutation = updateCommunicationNote;
        }
        if (!!form.state.Attachments && form.state.Attachments.length > 0) {
            form.state.Attachments.forEach(attachment => {
                if (!!attachment.ID) {
                    data.Attachments.push({
                        ID: attachment.ID
                    });
                }
            });
        }
        return await getClient().mutate({
            mutation,
            variables: {
                input: data
            }
        });
    };

    sendCommunicationNoteFunction = async () => {
        // assume it is already saved before we send, so attachments are not lost.
        const { recordID, recordType } = this.props;
        const form = this.form;
        const data = {
            [recordType + 'ID']: recordID,
            ID: form.state.ID,
            ScheduledToSendEmail: true
        };

        return await getClient().mutate({
            mutation: updateCommunicationNote,
            variables: {
                input: data
            }
        });
    };

    handleCommunicationNoteTemplateChange = event => {
        const { communicationNoteTemplates, LockoutWarning } = this.state;
        const form = this.form;

        let communicationNoteTemplate = communicationNoteTemplates[event.target.value];

        if (!communicationNoteTemplate && !!LockoutWarning) {
            this.setState({ LockoutWarning: false });
            form.context.forceUpdate();
            return;
        }
        if (!!communicationNoteTemplate) {
            const { substitutionFields } = this.props;
            form.state.Title = applyShortcodes(communicationNoteTemplate.Title, substitutionFields);
            form.state.Description = applyShortcodes(communicationNoteTemplate.Description, substitutionFields);
            form.state.Attachments = communicationNoteTemplate.Attachments;
            this.setState({ LockoutWarning: !!communicationNoteTemplate.AddLinkToFuneralArranger });
        }
        form.context.forceUpdate();
    };

    renderAttachmentLinks(attachments, readOnly) {
        return (
            <Grid item>
                <h4> Attached Files </h4>
                <FlexGrid>
                    {attachments.map(attachment => (
                        <ThumbnailCard
                            key={'file-' + attachment.ID}
                            fileName={attachment.Name}
                            fileLink={attachment.AbsoluteLink}
                            fileId={attachment.ID}
                            onClickRemoveAction={!readOnly ? () => this.onAttachmentRemove(attachment.ID) : undefined}
                        />
                    ))}
                </FlexGrid>
            </Grid>
        );
    }

    handleUploadAttachment(uploadFile, attachments) {
        const form = this.form;
        attachments = attachments || [];

        attachments.push({
            ID: uploadFile.ID,
            AbsoluteLink: uploadFile.AbsoluteLink,
            Name: getFileName(uploadFile.FileName)
        });

        form.setField({
            Attachments: attachments
        });
    }

    onAttachmentRemove(fileId) {
        const form = this.form;

        let Attachments = form.getField('Attachments');
        Attachments = Attachments.filter(obj => fileId !== obj.ID);

        form.setField({ Attachments });
    }

    refetchModal = () => null;
    refetchList = () => null;
}

const queryCommunicationNoteTemplates = gql`
    ${CommunicationNoteTemplate}
    query($limit: Int) {
        readCommunicationNoteTemplates: readCMCommunicationNoteTemplates(limit: $limit) {
            edges {
                node {
                    ...CommunicationNoteTemplate
                }
            }
        }
    }
`;

const createCommunicationNote = gql`
    ${CommunicationNote}
    mutation createCommunicationNote($input: CreateCMCommunicationNoteInput!) {
        createCommunicationNote: createCMCommunicationNote(input: $input) {
            ...CommunicationNote
        }
    }
`;

const updateCommunicationNote = gql`
    ${CommunicationNote}
    mutation updateCommunicationNote($input: UpdateCMCommunicationNoteInput!) {
        updateCommunicationNote: updateCMCommunicationNote(input: $input) {
            ...CommunicationNote
        }
    }
`;

const queryCommunicationNote = gql`
    ${CommunicationNote}
    query readOneCommunicationNote($noteId: ID!) {
        readOneCommunicationNote: readOneCMCommunicationNote(ID: $noteId) {
            ...CommunicationNote
        }
    }
`;

const summaryFragment = gql`
    fragment CommunicationNoteSummary on CMCommunicationNote {
        ID
        Created
        Title
        EmailSent
        EmailSentAt
        SentEmailTo
        MailDate
        MailTime
    }
`;

const queryMyBothNotes = gql`
    ${summaryFragment}
    ${AuthorisedContact}
    query readCommunicationNotes($creID: ID!, $plqID: ID!) {
        creNotes: readCMCommunicationNotes(filter: { CremationOrderID: { eq: $creID }}) {
            edges {
                node {
                    ...CommunicationNoteSummary
                }
            }
        }
        plqNotes: readCMCommunicationNotes(filter: { PlaqueOrderID: { eq: $plqID }}) {
            edges {
                node {
                    ...CommunicationNoteSummary
                }
            }
        }
        creRecord: readOneCremationOrder(ID: $creID) {
            ID
            Client {
                ID
                Email
                MailingAddress
            }
            Business {
                ID
                Email
            }
            AuthorisedContacts {
                ...AuthorisedContact
            }
        }
        plqRecord: readOnePlaqueOrder(ID: $plqID) {
            ID
            Client {
                ID
                Email
                MailingAddress
            }
            Business {
                ID
                Email
            }
            AuthorisedContacts {
                ...AuthorisedContact
            }
        }
    }
`;
const styles = ({ palette, spacing, breakpoints }) => ({
    root: {
        maxWidth: '800px',
        width: '100%',
        maxHeight: 'unset',
        [breakpoints.down('sm')]: {
            margin: spacing(1)
        }
    },
    diagHeader: {
        background: palette.primary.main,
        padding: spacing(3)
    },
    diagHeaderTitle: {
        color: '#FFFFFF',
        fontSize: '16px',
        fontWeight: 'lighter',
        lineHeight: 'normal'
    },
    diagHeaderSubtitle: {
        color: '#FFFFFF',
        fontSize: '36px',
        fontWeight: 'initial',
        lineHeight: 'normal'
    },
    diagHeaderIcon: {
        width: '70px',
        height: '70px',
        float: 'right',
        color: 'rgba(255,255,255,0.5)'
    },
    content: {
        width: '100%',
        padding: spacing(3),
        maxHeight: 'calc(100vh - 15rem)',
        overflow: 'auto',
        minHeight: 340,
        backgroundColor: '#fafafa',
        [breakpoints.down('sm')]: {
            maxHeight: 'calc(100vh - 12rem)',
            padding: spacing(1)
        }
    },
    diagActions: {
        padding: spacing(3),
        paddingTop: spacing(2),
        margin: 0,
        justifyContent: 'space-between'
    },
    tickIcon: { color: palette.action.save }
});
export default compose(withSnackbarMessage, withStyles(styles))(CommunicationNotes);
